import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import { useGetAllStaffQuery } from "../../../store/appApi";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const ResetPassword = () => {
  const role = useSelector(selectUserRole);
  const navigate = useNavigate();

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: staffResponse } = useGetAllStaffQuery({ page: 1, limit: 500 });
  const staffList = Array.isArray(staffResponse?.staff) ? staffResponse.staff : [];

  const employeeOptions = staffList.map((emp) => ({
    value: emp._id,
    label: `${emp.name} — ${emp.role}${emp.mobile ? ` (${emp.mobile})` : ""}`,
  }));

  if (role !== "super-admin") {
    return (
      <div className="container mt-5 text-center">
        <h4 className="text-danger">Access Denied</h4>
        <p>This page is only accessible to super admins.</p>
      </div>
    );
  }

  const validate = () => {
    const errs = {};
    if (!selectedEmployee) errs.employee = "Please select an employee.";
    if (!newPassword) errs.newPassword = "New password is required.";
    else if (newPassword.length < 8) errs.newPassword = "Password must be at least 8 characters.";
    if (!confirmPassword) errs.confirmPassword = "Please confirm the password.";
    else if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/reset-employee-password`, {
        userId: selectedEmployee.value,
        password: newPassword,
      });
      Swal.fire("Success", "Password has been reset successfully.", "success");
      setSelectedEmployee(null);
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to reset password.";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Reset Password" motherMenu="Staff" />
      <div className="row">
        <div className="col-xl-8 col-lg-10 mx-auto">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Reset Employee Password</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group mb-4">
                  <label className="form-label font-w600">
                    Select Employee <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={employeeOptions}
                    value={selectedEmployee}
                    onChange={setSelectedEmployee}
                    placeholder="Search and select employee..."
                    isSearchable
                    classNamePrefix="react-select"
                  />
                  {errors.employee && (
                    <div className="text-danger mt-1 small">{errors.employee}</div>
                  )}
                </div>

                <div className="form-group mb-4">
                  <label className="form-label font-w600">
                    New Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className={`form-control${errors.newPassword ? " is-invalid" : ""}`}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                  />
                  {errors.newPassword && (
                    <div className="invalid-feedback">{errors.newPassword}</div>
                  )}
                </div>

                <div className="form-group mb-4">
                  <label className="form-label font-w600">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className={`form-control${errors.confirmPassword ? " is-invalid" : ""}`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                  {errors.confirmPassword && (
                    <div className="invalid-feedback">{errors.confirmPassword}</div>
                  )}
                </div>

                <div className="d-flex gap-3">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
